import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './SignatureModal.css';

function SignatureModal({ isOpen, onClose, onSave, initialDataUrl }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [mode, setMode] = useState('draw');
  const [uploadedDataUrl, setUploadedDataUrl] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#4A4A4A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#132030';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (initialDataUrl) {
      setMode('upload');
      setUploadedDataUrl(initialDataUrl);
    } else {
      setMode('draw');
      setUploadedDataUrl('');
    }
  }, [isOpen, initialDataUrl]);

  const getCanvasPos = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const point = event.touches ? event.touches[0] : event;
    return {
      x: point.clientX - rect.left,
      y: point.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (mode !== 'draw') return;
    if (e.touches) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getCanvasPos(e);
    if (!pos) return;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || mode !== 'draw') return;
    if (e.touches) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getCanvasPos(e);
    if (!pos) return;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#132030';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleClearUpload = () => setUploadedDataUrl('');
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setUploadedDataUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (mode === 'draw') {
      const canvas = canvasRef.current;
      if (canvas) onSave(canvas.toDataURL('image/png'));
    } else if (uploadedDataUrl) {
      onSave(uploadedDataUrl);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="signature-modal-overlay" onClick={onClose}>
      <div className="signature-modal" onClick={(e) => e.stopPropagation()}>
        <div className="signature-modal-header">
          <h3>{t('dataPanel.signature')}</h3>
          <button type="button" className="signature-close" onClick={onClose}>×</button>
        </div>
        <div className="signature-tabs">
          <button
            type="button"
            className={`signature-tab ${mode === 'draw' ? 'active' : ''}`}
            onClick={() => setMode('draw')}
          >
            {t('dataPanel.drawSignature')}
          </button>
          <button
            type="button"
            className={`signature-tab ${mode === 'upload' ? 'active' : ''}`}
            onClick={() => setMode('upload')}
          >
            {t('dataPanel.uploadSignature')}
          </button>
        </div>
        <div className="signature-modal-body">
          {mode === 'draw' ? (
            <div className="signature-canvas-wrapper">
              <canvas
                ref={canvasRef}
                width={520}
                height={200}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              <button type="button" className="signature-clear" onClick={clearCanvas}>
                {t('dataPanel.clear')}
              </button>
            </div>
          ) : (
            <div className="signature-upload">
              <input type="file" accept="image/*" onChange={handleUpload} />
              {uploadedDataUrl && (
                <>
                  <img src={uploadedDataUrl} alt={t('dataPanel.signature')} className="signature-preview" />
                  <button type="button" className="signature-clear" onClick={handleClearUpload}>
                    {t('dataPanel.clear')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="signature-modal-footer">
          <button type="button" className="signature-cancel" onClick={onClose}>
            {t('dataPanel.cancel')}
          </button>
          <button type="button" className="signature-save" onClick={handleSave}>
            {t('dataPanel.saveSignature')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignatureModal;
